import { courseObjectKey } from "../shared/storageLayout.mjs";
import { backfillTrainingCache, cachedScopedFetch, clearScopedCache, getScopedCacheInfo, invalidateTrainingCacheSession, type CacheRole } from "./trainingCache";

// Curriculum and engineering files now share the same account-scoped store and
// cross-tab clear barrier. Every hit still requires a fresh authorized request.
const pending = new Map<string, Promise<unknown>>();
let sessionEpoch = 0;

export async function clearCourseCache(accountId?: string) {
  sessionEpoch++;
  pending.clear();
  if (accountId) await invalidateTrainingCacheSession(accountId);
}
export const getCourseCacheInfo = (accountId: string, role: CacheRole) => getScopedCacheInfo(accountId, "curriculum", role);
export const clearCourseCacheForUser = (accountId: string, role: CacheRole) => clearScopedCache(accountId, "curriculum", role);
export const backfillCourseCacheToDirectory = (accountId: string, role: CacheRole) => backfillTrainingCache(accountId, role);

export async function cachedCourse<T>(
  accountId: string, role: CacheRole, caseId: "a" | "b",
  request: (etag?: string) => Promise<Response>, stillCurrent: () => boolean,
): Promise<T> {
  const key = courseObjectKey(accountId, role, caseId);
  const epoch = sessionEpoch;
  const current = () => epoch === sessionEpoch && stillCurrent();
  // Share concurrent requests only within the same mounted session. The caller
  // is checked again after await, even if another view began the request.
  const existing = pending.get(key) as Promise<T> | undefined;
  if (existing) {
    const value = await existing;
    if (!current()) throw new DOMException("课程会话已变更，请重新打开大纲。", "AbortError");
    return value;
  }
  const task = (async () => {
    const response = await cachedScopedFetch({ accountId, role, category: "curriculum", key, request, stillCurrent: current });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "课程读取失败，请稍后重试。");
    }
    const payload = await response.json();
    if (!current()) throw new DOMException("课程会话已变更，请重新打开大纲。", "AbortError");
    if (payload.caseId !== caseId || !Array.isArray(payload.days)) throw new Error("教学案例内容不匹配。");
    return payload as T;
  })();
  pending.set(key, task);
  try { return await task; }
  finally { if (pending.get(key) === task) pending.delete(key); }
}
